import { useEffect, useState } from "react";

/** Fit mobile app surfaces to the visible viewport while a text keyboard is open. */
export function useStartPageKeyboardViewport(enabled: boolean) {
  const [viewportSize, setViewportSize] = useState<{ height: number; top: number } | null>(null);
  useEffect(() => {
    setViewportSize(null);
    if (!enabled || !window.visualViewport) return;
    const viewport = window.visualViewport;
    let frame = 0;
    let fullHeight = window.innerHeight;
    let layoutWidth = window.innerWidth;
    let keyboardActive = false;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const editing = document.activeElement?.matches('textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="password"], input[type="tel"], input[type="url"], input[type="number"], [contenteditable="true"]');
        // Rotation changes the layout width as well as its full height. Rebase
        // even with the keyboard open so a portrait baseline cannot trap landscape.
        if (window.innerWidth !== layoutWidth) {
          layoutWidth = window.innerWidth;
          fullHeight = window.innerHeight;
        } else if (!editing && !keyboardActive) fullHeight = window.innerHeight;
        const keyboardOpen = window.matchMedia("(max-width: 1023px)").matches &&
          Math.abs(viewport.scale - 1) < 0.01 && (editing || keyboardActive) && Math.max(fullHeight, window.innerHeight) - viewport.height > 120;
        // Blur fires before the iOS keyboard finishes closing. Keep the fitted
        // shell until the visible viewport recovers, including the Done button.
        keyboardActive = Boolean(keyboardOpen);
        setViewportSize(keyboardOpen ? { height: viewport.height, top: viewport.offsetTop } : null);
      });
    };
    window.addEventListener("resize", update);
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    document.addEventListener("focusin", update);
    document.addEventListener("focusout", update);
    update();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", update);
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.removeEventListener("focusin", update);
      document.removeEventListener("focusout", update);
    };
  }, [enabled]);
  // Navigation must release the fixed shell on the very first destination render.
  return enabled ? viewportSize : null;
}
